import React from 'react';
import ReactDOM from 'react-dom/client';
import {ConfigProvider} from "antd";
import {CookiesProvider} from "react-cookie";
import {Provider} from "react-redux";
import {createBrowserRouter, RouterProvider, useParams, useLocation, Outlet} from "react-router-dom";
import {Main} from "./Main";
import {store} from "./store/store";
import {Game} from "./Game";
import {GamePage} from "./GamePage";
import {NotFound} from "./NotFound";

const VALID_LANGS = ['en', 'ru', 'he'];
const GA_ID = 'G-2LBBH3J5ME';

// Bootstrap gtag — sets up window.dataLayer and the gtag() function.
// The external gtag.js script (loaded async in index.html) will pick up
// the existing dataLayer array, so order doesn't matter.
function initGtag() {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    // Must use `arguments`, not rest params — GA4 dataLayer distinguishes Arguments from Array.
    if (!w.gtag) {
        w.gtag = function gtag() { w.dataLayer.push(arguments); };
    }
    w.gtag('js', new Date());
    w.gtag('config', GA_ID);
}

// Fires a GA4 page_view on every client-side navigation.
const Analytics = () => {
    const location = useLocation();
    React.useEffect(() => {
        initGtag();
    }, []); // run once on mount to initialise
    React.useEffect(() => {
        const w = window as any;
        if (typeof w.gtag === 'function') {
            w.gtag('config', GA_ID, { page_path: location.pathname + location.search });
        }
    }, [location]);
    return null;
};

// Guards a /:lang or /:lang/:game path — shows 404 immediately for unknown lang codes.
const LangRoute = ({ element }: { element: React.ReactElement }) => {
    const { lang } = useParams<{ lang: string }>();
    return VALID_LANGS.includes(lang ?? '') ? element : <NotFound/>;
};

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

const Root = () => <><Analytics/><Outlet/></>;

const router = createBrowserRouter([
        {
            element: <Root/>,
            children: [
                {
                    path: "/",
                    element: <Main/>,
                },
                {
                    // /:lang with no game — validate lang, show Main or 404
                    path: "/:lang",
                    element: <LangRoute element={<Main/>}/>,
                },
                {
                    // /:lang/:game — validate lang, show GamePage or 404
                    path: "/:lang/:game",
                    element: <LangRoute element={<GamePage/>}/>,
                },
                {
                    path: "*",
                    element: <NotFound/>,
                },
            ],
        },
    ], {
        future: {
            v7_skipActionErrorRevalidation: true,
            v7_fetcherPersist: true,
            v7_relativeSplatPath: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
        },
    })
;

root.render(
    <ConfigProvider theme={{token: {fontFamily: 'Nunito, sans-serif',}}}>
        <CookiesProvider>
            <Provider store={store}>
                {/*<React.StrictMode>*/}
                <svg width="0" height="0" style={{position: 'absolute'}}>
                    <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop stopColor="#c611e1" offset="0%"/>
                        <stop stopColor="#13eccb" offset="100%"/>
                    </linearGradient>
                </svg>
                <RouterProvider router={router} future={{
                    v7_startTransition: true,
                }}/>
                {/*</React.StrictMode>*/}
            </Provider>
        </CookiesProvider>
    </ConfigProvider>
);
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals

